import { FC, useEffect, useMemo, useState } from "react";
import { Team, useApiCall, useApiEndpoint } from "@jane/lib/src/api";
import { config } from "../../config";
import { usePermissions } from "../../resources/PermissionsHook";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ImageUpload } from "@jane/lib/src/components/form/ImageUpload";
import { TeamSpace, TeamSpacePermisssion } from "@jane/lib/src/api/repositories/documentation-library-repository";
import { Input } from "@jane/lib/src/components/form/Input";
import { ContentContainer } from "@jane/lib/src/components/content/ContentContainer";
import { Card } from "@jane/lib/src/components/Card";
import { Loading } from "@jane/lib/src/components/Loading";
import { PageHeader } from "@jane/lib/src/components/content/PageHeader";
import {Button, IconButton} from "@jane/lib/src/components/form/Button";
import { useTeamMap } from "../../components/team-select";
import {faChevronDown, faMinus, faPeopleGroup, faPlus, faSquareCheck} from "@fortawesome/free-solid-svg-icons";
import { Autocomplete } from "@jane/lib/src/components/form/auto-complete/Autocomplete";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSquare} from "@fortawesome/free-regular-svg-icons";
import {faChevronRight, faCircleMinus} from "@fortawesome/pro-solid-svg-icons";
import {getSubTeamsRecursively} from "../learn/resolveSubTeams";

export const DocumentationLibrarySpaceSettingsPage: FC = () => {
  const { spaceId } = useParams();
  const { canManageTeamSpaces } = usePermissions();

  const { documentations, teams } = useApiCall(config);
  const { resource: teamsResource, isLoading: isLoadingTeamsResource } = useApiEndpoint(() => teams.all(), canManageTeamSpaces);
  const { resource: teamSpaceResource, isLoading: isLoadingTeamSpaceResource } = useApiEndpoint(() =>
    documentations.readTeamSpace(spaceId || ""),
  );
  const navigate = useNavigate();

  console.log(teamSpaceResource)

  const [teamSpace, setTeamSpace] = useState<TeamSpace>();
  useEffect(() => {
    setTeamSpace(teamSpaceResource);
  }, [teamSpaceResource]);

  const save = async () => {
    if (canManageTeamSpaces && teamSpace) {
      await documentations.editTeamSpace(
        teamSpace.id,
        teamSpace.ons_team_id,
        teamSpace.name,
        teamSpace.icon ?? undefined,
        teamSpace.image ?? undefined,
        teamSpace.permissions,
      );
    }

    await navigate("/samen-werken");
  };

  return (
    <ContentContainer>
      <Link className={"underline cursor-pointer text-brand-primary"} to="/samen-werken">
        Terug naar overzicht
      </Link>
      <PageHeader>Werkruimte: {teamSpaceResource?.name}</PageHeader>
      <Loading loading={isLoadingTeamsResource && isLoadingTeamSpaceResource}>
        {teamSpace && teamsResource && (
          <div className="flex flex-col gap-4">
            <GlobalSettings teamSpace={teamSpace} setTeamSpace={(s) => setTeamSpace(s)} />
            <PermissionsCard teamSpace={teamSpace} setTeamSpace={(s) => setTeamSpace(s)} teams={teamsResource} />
            <div>
              <Button type={"primary"} size={"sm"} text="Opslaan" onClick={() => save()} />
            </div>
          </div>
        )}
      </Loading>
    </ContentContainer>
  );
};

const GlobalSettings: FC<{ teamSpace: TeamSpace; setTeamSpace: (teamSpace: TeamSpace) => void }> = ({
  teamSpace,
  setTeamSpace,
}) => {
  const { documentations } = useApiCall(config);
  const { canEditTeamSpaceImage } = usePermissions();

  const upload = async (file: File | null) => {
    if (canEditTeamSpaceImage && file != null) {
      await documentations.editTeamSpaceImage(teamSpace.id, file);
    }
  };

  return (
    <Card title="Algemeen">
      <div className={'flex flex-row justify-between'}>
        <div className={"flex flex-1 flex-col space-y-4"}>
          <Input type={"text"} label={"Naam"} value={teamSpace.name} onChange={(name) => setTeamSpace({ ...teamSpace, name })}/>
          {canEditTeamSpaceImage && <ImageUpload withPreview={true} onFileSelect={(f) => upload(f)} />}
        </div>

        {teamSpace.image ? <div className={'border border-brand-primary rounded-xl overflow-hidden mr-4'}>
          <img className={'w-40 h-40'} src={teamSpace.image} alt={'foto_werkruimte'}/>
        </div> : <div className={'w-40 h-40 flex items-center justify-center bg-brand-50 border border-brand-primary rounded-xl text-[40px] overflow-hidden mr-4 shadow-md'}>
          <FontAwesomeIcon icon={faPeopleGroup} />
        </div>}

      </div>
    </Card>
  );
};

const PermissionsCard: FC<{ teamSpace: TeamSpace; setTeamSpace: (teamSpace: TeamSpace) => void; teams: Team[] }> = ({teamSpace, setTeamSpace, teams}) => {
  return <Card title="Toegang">
      <div className="">
        <div className="flex flex-col space-y-4 w-full">

          <div className={"flex flex-row items-center space-x-2"}>
            <div className={'flex flex-1'}>
              <TeamSelect label="" teams={teams} value={teamSpace.ons_team_id}
                          onChange={(ons_team_id) => setTeamSpace({ ...teamSpace, ons_team_id: ons_team_id ?? 0 })}/>
            </div>

            <div className={'flex flex-row px-3 py-1.5 gap-x-2 mt-1 items-center border-2 border-slate-200 bg-slate-100 opacity-75 text-black rounded-md hover:cursor-not-allowed'}>
              <FontAwesomeIcon icon={faSquareCheck} />
              <div>Lezen</div>
            </div>

            <div className={'flex flex-row px-3 py-1.5 gap-x-2 mt-1 items-center border-2 border-slate-200 bg-slate-100 opacity-75 text-black rounded-md hover:cursor-not-allowed'}>
              <FontAwesomeIcon icon={faSquareCheck} />
              <div>Beheren</div>
            </div>

            <div className={'mr-10'}></div> {/* Offset for remove button since main team can't be removed */}
          </div>

          {teamSpace.permissions.map((permission, k) => {
            return <PermissionRow key={k} isMainTeam={false} index={k} permission={permission} teamSpace={teamSpace} setTeamSpace={setTeamSpace} teams={teams} />
          })}

        </div>
      </div>

      <div className="mt-6">
        <Button type={"primary"} size={"xs"} text="Voeg toe" icon={faPlus}
          onClick={() =>
            setTeamSpace({
              ...teamSpace,
              permissions: [...teamSpace.permissions, new TeamSpacePermisssion(teamSpace.id, null, "reader", false)],
            })
          }
        />
      </div>
    </Card>
};

const PermissionRow: FC<{ index: number, isMainTeam: boolean, permission: TeamSpacePermisssion, teamSpace: TeamSpace; setTeamSpace: (teamSpace: TeamSpace) => void; teams: Team[] }> = ({index, isMainTeam, permission, teamSpace, setTeamSpace, teams}) => {
  const [canRead, setCanRead] = useState<boolean>(true)
  const [canManage, setCanManage] = useState<boolean>(permission.type === 'editor')
  const [includeSubteams, setIncludeSubteams] = useState<boolean>(permission.includeSubteams ?? false)

  useMemo(() => {
    setTeamSpace({
      ...teamSpace,
      permissions: teamSpace.permissions.map((item, i) =>
        i === index ? { ...item, type: canManage ? "editor" : "reader", includeSubteams: includeSubteams } : item,
      ),
    })
    console.log(permission)
  }, [canRead, canManage, includeSubteams])

  const subTeams = useMemo(() => {
    if (includeSubteams && permission.onsTeamId !== null) {
      const matchTeam = teams.find((t) => t.id === permission.onsTeamId)

      if (matchTeam === null) {
        return null;
      }

      const subteams = getSubTeamsRecursively(teams, matchTeam!.id, 20)
      return subteams.filter((subT) => subT.parentObjectId === matchTeam!.id)
    } else {
      return null;
    }
  }, [includeSubteams, permission])

  return <div>
    <div className={"flex flex-row items-center space-x-2"}>
      <div className={'flex flex-1'}>
        <TeamSelect label={''} teams={teams} value={permission.onsTeamId ?? 0}
                    onChange={(onsTeamId) =>
                      setTeamSpace({
                        ...teamSpace,
                        permissions: teamSpace.permissions.map((item, i) => (i === index ? { ...item, onsTeamId } : item)),
                      })
                    }
        />
      </div>

      <div className={'flex flex-row px-3 py-1.5 gap-x-2 mt-1 items-center bg-white border-2 border-slate-200 hover:bg-slate-100 hover:cursor-default rounded-md'} onClick={(value) => setIncludeSubteams(!includeSubteams)}>
        <FontAwesomeIcon icon={includeSubteams ? faSquareCheck : faSquare} />
        <div>Inclusief Subteams</div>
      </div>

      <div className={'flex flex-row px-3 py-1.5 gap-x-2 mt-1 items-center border-2 border-slate-200 bg-slate-100 opacity-75 text-black rounded-md hover:cursor-not-allowed'}>
        <FontAwesomeIcon icon={faSquareCheck} />
        <div>Lezen</div>
      </div>

      <div className={'flex flex-row px-3 py-1.5 gap-x-2 mt-1 items-center bg-white border-2 border-slate-200 hover:bg-slate-100 hover:cursor-default rounded-md'} onClick={() => setCanManage(!canManage)}>
        <FontAwesomeIcon icon={canManage ? faSquareCheck : faSquare} />
        <div>Beheren</div>
      </div>

      <div className={'mt-1'}>
        <IconButton icon={faCircleMinus} size={'sm'} type={'danger'} disabled={isMainTeam}
                    onClick={() =>
                      setTeamSpace({
                        ...teamSpace,
                        permissions: teamSpace.permissions.toSpliced(index, 1),
                      })
                    }
        />
      </div>

    </div>
    {includeSubteams && <div className={'ml-2 pl-2 mt-2 pb-2 border-l-2 border-brand-secondary'}>
      <div className={'text-brand-900 text-sm font-medium mb-2 '}>Onderliggende teams: {subTeams &&
        <span>({subTeams.length})</span>}</div>
      {subTeams?.map((st, j) => {
        return <SubTeamRow key={j} team={st} teams={teams}/>
      })}
    </div>}
  </div>
}

const SubTeamRow: FC<{ team: Team, teams?: Team[], indentationLevel?: number }> = ({ team, teams, indentationLevel = 0 }) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true)

  const subTeams = useMemo(() => {
    if (teams === null) {
      return [];
    } else {
      return teams!.filter((t) => t.parentObjectId === team.id)
    }
  }, [team, teams])

  return <div>
    <div className={`flex items-center space-x-4 my-1 px-2 py-1 hover:bg-brand-200 rounded-md`} onClick={() => setIsCollapsed(!isCollapsed)}
         style={{marginLeft: `calc(${indentationLevel*20}px)`}}>
      <div className={'flex-1 text-sm'}>{team.name}</div>
      {subTeams.length > 0 && <div>({subTeams.length})</div>}
      {subTeams.length > 0 && <FontAwesomeIcon className={'mr-2'} icon={isCollapsed ? faChevronRight : faChevronDown} />}
    </div>

    {!isCollapsed && subTeams.map((subT, l) => {
      return <SubTeamRow key={l} team={subT} teams={teams} indentationLevel={indentationLevel + 1} />
    })}
  </div>
}


export const TeamSelect: FC<{ label: string, teams: Team[], value: number | null, onChange: (t: number | null) => void }> = ({ label, teams, value, onChange }) => {
  const { teams: teamsApi } = useApiCall(config);
  const { canListTeams, canListAllTeams, canManageTeamSpaces } = usePermissions();
  const { resource: teamResource } = useApiEndpoint(() => teamsApi.myTeam(), canListTeams);
  const { resource: secondaryTeams } = useApiEndpoint(() => teamsApi.myTeams(), canListTeams);
  const { resource: allTeams } = useApiEndpoint(() => teamsApi.all(), canManageTeamSpaces);

  const teamMap = useTeamMap(teams, teamResource ?? null, canManageTeamSpaces ? allTeams ?? [] : secondaryTeams ?? []);
  const teamFilterOptions = useMemo(() => {
    const entries = teamMap.map((teamsList) => {
      const team = teamsList[teamsList.length - 1];
      const indentation = teamsList.length;
      const prefix = canListAllTeams ? "\xA0\xA0".repeat(indentation) : "";

      return [team.id.toString(), prefix + team.name];
    });

    return {
      '-1': 'Selecteer team',
      ...Object.fromEntries(entries)
    };
  }, [teamMap, canListAllTeams]);

  return (
    <Autocomplete label={label} width={'full'} options={teamFilterOptions} value={value ? value.toString() : "-1"}
      onChange={(val) => onChange(val === "-1" ? null : Number(val))}
    />
  );
};
